import React, { useEffect, useState } from "react"
import { useForm, Controller } from "react-hook-form"
import { z } from "zod"
import { zodResolver } from "@hookform/resolvers/zod"
import axios from 'axios'
import { toast } from 'react-hot-toast';
import { MdHighlightOff, MdClose } from "react-icons/md";
import { useNavigate } from "react-router-dom"

import { Header } from "../../components/Header"
import { ButtonContainer, Container, Content, FileContainer, Footer, FooterContainer, Form, InfoContainer, InputContainer, ParagraphContainer, SelectContainer, TitleContainer, ToastContainer, ToastText } from "./styles"
import { Typography } from "../../components/Typography"
import { Button } from "../../components/Button"

import { ReactComponent as Info } from "../../assets/icon/info.svg"
import { Input } from "../../components/Input"
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from "../../components/Select"
import { TextArea } from "../../components/TextArea"
import { isValidCPF, isValidEmail } from "../../utils/validators"

const schema = z.object({
  cpf: z.string().nonempty("CPF é obrigatório.").refine((value) => isValidCPF(value), "CPF inválido."),
  name: z.string().nonempty("Nome é obrigatório."),
  email: z.string().refine((value) => isValidEmail(value), "Email inválido."),
  phone: z.string().nonempty("Celular é obrigatório."),
  reason: z.string().nonempty("Razão é obrigatório."),
  otherReason: z.string().optional(),
  documentFront: z.custom(), 
  documentBack: z.custom(),
})

function UnsubscribeScreen() {
  const {
    register,
    handleSubmit,
    control,
    watch,
    formState: { errors, isSubmitting },
  } = useForm({ resolver: zodResolver(schema)})

  const [urlBackend, setUrlBackend] = useState()
  const navigate = useNavigate()

  const onSubmit = async (data) => {    
    try {
      const formData = new FormData()

      formData.append("frenteDocumento", data.documentFront[0]);
      formData.append("versoDocumento", data.documentBack[0]);
      formData.append("dados", JSON.stringify({
        cpf: data.cpf,
        nomeCompleto: data.name,
        email: data.email,
        celular: data.phone,
        motivo: data.reason,
        outro: data.otherReason,
      }))
      await axios.post(urlBackend + 'crefisa-mais/deleteaccount', formData, { headers: { 'Content-Type': 'multipart/form-data' }})

      navigate("/descadastrar/sucesso")

    } catch (err){
      toast(
        <ToastContainer> 
          <Typography style={{paddingTop: 10, paddingBottom: 10}} size="small" color="white">{err.message}</Typography>
          <MdClose size={24} color='white'/>
        </ToastContainer>,
        {
          icon: <MdHighlightOff size={24} color='white'/>,
          style: {
            background: '#F67D88',
            paddingLeft: '1rem',
            paddingRight: '1rem',
            paddingTop: 0,
            paddingBottom: '0.25rem',
            borderRadius: '0 0 0.5rem 0.5rem',
            color: 'white',
            width: '375px',
            margin: '0px'
          },
        }
      );
    }
  }

  useEffect(() => { 
    let urlBase = window.location.href;
    if (urlBase.indexOf('exclusaoconta.') > -1 ){
      setUrlBackend('https://www.crefisa.com.br/')
    } else {
        setUrlBackend('https://www2-h.crefisa.com.br/')
    }
  })

  return (
    <Container>
      <Header />
      <Content>
        <Form onSubmit={handleSubmit(onSubmit)}>
          <TitleContainer>
            <Typography size="large" weight="bold">Você tem certeza de que deseja solicitar a exclusão de conta do aplicativo Crefisa+?</Typography>
            <ParagraphContainer>
              <Typography weight="bold">Todos os seus dados no aplicativo serão deletados do nosso sistema.</Typography>
              <Typography>Após a exclusão, você poderá acessar os produtos e serviços já contratados pela nossa Central de Relacionamento, nos telefones 4004 4001 (capitais e regiões metropolitanas) ou 0800 722 4444 (demais localidades), ou  ir a um Ponto de Atendimento Crefisa.</Typography>
              <Typography>Também será possível realizar um novo cadastro no aplicativo Crefisa+, após 24h da exclusão realizada.</Typography>
            </ParagraphContainer>
          </TitleContainer>
          <TitleContainer>
            <Typography size="large" weight="bold">Dados Pessoais</Typography>
            <Typography>Precisamos de alguns dados para excluir a sua conta no aplicativo Crefisa+.</Typography>
            <InputContainer>
              <Input.WrapperStandard register={register} name="cpf" label="CPF" mask="cpf" error={errors?.cpf?.message}/>
              <Input.WrapperStandard register={register} name="name" label="Nome Completo" error={errors?.name?.message}/>
              <Input.WrapperStandard register={register} name="email" label="E-mail" error={errors?.email?.message}/>
              <Input.WrapperStandard register={register} name="phone" label="Celular" mask="phone" error={errors?.phone?.message}/>
            </InputContainer>
          </TitleContainer>
          <TitleContainer>
            <Typography size="large" weight="bold">Qual o motivo da exclusão da conta?</Typography>
            <InputContainer>
              <SelectContainer>
              <Controller
                name="reason"
                control={control} 
                render={({ field }) => (
                  <Select onValueChange={field.onChange}>
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectGroup>
                        <SelectItem value="Privacidade">Privacidade</SelectItem>
                        <SelectItem value="Fim de relacionamento com o serviço">Fim de relacionamento com o serviço</SelectItem>
                        <SelectItem value="Dificuldade de usar o aplicativo">Dificuldade de usar o aplicativo</SelectItem>
                        <SelectItem value="Outros">Outros</SelectItem>
                      </SelectGroup>
                    </SelectContent>
                  </Select>
                )} 
              />
              </SelectContainer>
            </InputContainer>
            {watch("reason") === "Outros" && <TextArea label="Motivo" placeholder="Digite o motivo" { ...register("otherReason")}/>}
          </TitleContainer>
          <TitleContainer>
            <Typography size="large" weight="bold">Documentos</Typography>
            <ParagraphContainer>
              <Typography>Envie o mesmo documento de identificação utilizado para a abertura da conta. Siga as orientações para capturar as fotos.</Typography>
              <InfoContainer>
                <Info width={24} height={24} />
                <Typography size="small" color="textLight">Somente RG, CNH ou RNE serão aceitos.</Typography>
              </InfoContainer>
              <FileContainer>
                <div style={{display: 'flex', gap: 8, flexDirection: "column"}}>
                  <Typography size="medium" weight="bold">Frente do documento (tamanho máximo 5MB):</Typography>
                  <input id="front-document" type="file"  accept="image/jpeg,application/pdf" { ...register("documentFront") }/>
                </div>
                <div style={{display: 'flex', gap: 8, flexDirection: "column"}}>
                  <Typography size="medium" weight="bold">Verso do documento (tamanho máximo 5MB):</Typography>
                  <input id="front-document" type="file"  accept="image/jpeg,application/pdf" { ...register("documentBack") }/>
                </div>
              </FileContainer>
            </ParagraphContainer>
          </TitleContainer>
          <ButtonContainer>
            <Button disabled={isSubmitting} >Continuar</Button>
          </ButtonContainer>
        </Form>
      </Content>
      <FooterContainer>
        <Footer>
          <div>
            <Typography color="primary" weight="bold">
              CREFISA S.A. CRÉDITO FINANCIAMENTO E INVESTIMENTOS
            </Typography>
            <Typography color="textLight" size="small">CNPJ: 60.779.196/0001-96</Typography>
            <Typography size="small">Endereço: Rua Canadá, 387 - Jd. América, São Paulo - SP CEP: 01.436-900*</Typography>
          </div>
          <div>
            <Typography color="primary" weight="bold">BANCO CREFISA S.A.</Typography>
            <Typography color="textLight" size="small">CNPJ: 61.033.106/0001-86</Typography>
            <Typography size="small">Endereço: Rua Canadá, 390 - Jd. América, São Paulo - SP CEP: 01.436-900*</Typography>
          </div>
          <Typography size="small">*Nesses endereços, não há atendimento a clientes. Encontre nossos Pontos de Atendimento clicando aqui.</Typography>
        </Footer>
      </FooterContainer>
    </ Container>
  )
}

export default UnsubscribeScreen